import { ArrowDown, ArrowRight } from 'lucide-react';

const steps = [
  {
    badge: 'STEP 1',
    heading: 'Discover Your Values (5-15 min)',
    subheading: 'The Foundation',
    body: (
      <>
        <p className="mb-4">Choose your preferred method:</p>
        <ul className="space-y-1 mb-4 text-muted-foreground">
          <li>→ <span className="text-foreground font-medium">AI Conversation</span> (10-15 min) - Guided dialogue exploring what matters to you</li>
          <li>→ <span className="text-foreground font-medium">Values Card Sort</span> (5-8 min) - Prioritize 30 values into importance tiers</li>
          <li>→ <span className="text-foreground font-medium">Reflection Prompts</span> (8-12 min) - Write about meaningful career experiences</li>
          <li>→ <span className="text-foreground font-medium">Quick Assessment</span> (3-5 min) - Rate 15 statements for instant results</li>
        </ul>
        <p className="mb-4">
          <span className="font-medium text-foreground">Why values first?</span>{' '}
          Because generic goals (like "learn Python") feel hollow. Values-aligned goals (like "build technical depth because you value mastery") stick.
        </p>
        <p className="font-semibold text-primary">This unlocks everything else.</p>
      </>
    ),
    color: 'border-primary',
    bgTint: 'bg-primary/5',
  },
  {
    badge: 'STEP 2',
    heading: 'Set Values-Aligned Goals (10 min)',
    subheading: 'The Direction',
    body: (
      <>
        <p className="mb-4">Not arbitrary to-do lists. Goals structured by the proven <span className="font-semibold text-foreground">3E Framework</span>:</p>
        <ul className="space-y-1 mb-4 text-muted-foreground">
          <li>→ <span className="text-foreground font-medium">Education:</span> What to learn (courses, books, skills)</li>
          <li>→ <span className="text-foreground font-medium">Exposure:</span> Where to practice (projects, presentations, shadowing)</li>
          <li>→ <span className="text-foreground font-medium">Experience:</span> What to own (lead initiatives, ship products, manage teams)</li>
        </ul>
        <div className="bg-secondary/50 rounded-lg p-4 mb-4 font-mono text-sm">
          <p className="text-foreground font-medium mb-2">Example:</p>
          <p className="text-muted-foreground">Goal: "Build strategic influence"</p>
          <p className="text-muted-foreground pl-4">├─ Education: Complete "Influence Without Authority" course</p>
          <p className="text-muted-foreground pl-4">├─ Exposure: Present quarterly roadmap to leadership</p>
          <p className="text-muted-foreground pl-4">└─ Experience: Lead cross-functional initiative</p>
        </div>
        <p className="text-muted-foreground">
          The app gates features until you complete values—because goals without values are just noise.
        </p>
      </>
    ),
    color: 'border-accent',
    bgTint: 'bg-accent/5',
  },
  {
    badge: 'STEP 3',
    heading: 'Generate Your Development Plan (3 clicks)',
    subheading: 'The Roadmap',
    body: (
      <>
        <p className="mb-4 font-medium text-foreground">AI analyzes:</p>
        <ul className="space-y-1 mb-4 text-muted-foreground">
          <li>→ Your current role (e.g., "Product Manager")</li>
          <li>→ Your target role (e.g., "Senior PM" or "VP of Product")</li>
          <li>→ Your values (from Step 1)</li>
          <li>→ Your goals (from Step 2)</li>
          <li>→ Your skills (from your profile)</li>
        </ul>
        <p className="mb-4 font-medium text-foreground">Output: A personalized 6-month roadmap showing:</p>
        <ul className="space-y-1 mb-4 text-muted-foreground">
          <li>→ Skill gaps between current and target role</li>
          <li>→ Recommended focus areas mapped to the 3E Framework</li>
          <li>→ Milestone progression (e.g., "Master Technical Leadership: 0% → 75%")</li>
        </ul>
        <p className="font-semibold text-primary">Not a template. Generated uniquely for you.</p>
      </>
    ),
    color: 'border-warning',
    bgTint: 'bg-warning/5',
  },
  {
    badge: 'STEP 4',
    heading: 'Build Daily Habits (2 min/day)',
    subheading: 'The Momentum',
    body: (
      <>
        <p className="mb-4">Goals mean nothing without daily action. You break each goal into habits:</p>
        <div className="bg-secondary/50 rounded-lg p-4 mb-4 font-mono text-sm">
          <p className="text-foreground font-medium mb-2">Goal: "Improve technical communication"</p>
          <p className="text-muted-foreground pl-4">├─ Habit: Write one technical decision doc per week</p>
          <p className="text-muted-foreground pl-4">├─ Habit: Share one learning in team Slack daily</p>
          <p className="text-muted-foreground pl-4">└─ Habit: Present at engineering all-hands monthly</p>
        </div>
        <p className="text-muted-foreground">
          Track your streak. See the compounding effect. Small consistent actions become career transformation.
        </p>
      </>
    ),
    color: 'border-success',
    bgTint: 'bg-success/5',
  },
];

const HowItWorks = () => {
  return (
    <section className="py-20 md:py-28 bg-secondary/30">
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            From Stuck to Systematic in <span className="gradient-text">4 Steps</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Most career tools start with generic goals. We start with what matters to you.
          </p>
        </div>

        {/* Timeline Steps */}
        <div className="max-w-3xl mx-auto relative">
          {/* Vertical connecting line */}
          <div className="absolute left-6 md:left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary via-accent to-success hidden md:block" />

          <div className="space-y-6">
            {steps.map((step, index) => (
              <div key={step.badge} className={`relative ${step.bgTint} rounded-2xl`}>
                {/* Timeline dot */}
                <div className={`absolute left-4 md:left-6 top-6 w-4 h-4 rounded-full ${step.color.replace('border-', 'bg-')} ring-4 ring-background hidden md:block z-10`} />
                
                {/* Card */}
                <div className={`md:ml-16 p-6 md:p-8 rounded-2xl border-l-4 ${step.color} bg-card`}>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${step.color.replace('border-', 'bg-')}/20 ${step.color.replace('border-', 'text-')}`}>
                      {step.badge}
                    </span>
                    <span className="text-sm text-muted-foreground font-medium">{step.subheading}</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-display font-semibold mb-4">{step.heading}</h3>
                  <div className="text-muted-foreground leading-relaxed">
                    {step.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Flow Diagram */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-card border border-border/50 rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-2 text-sm font-medium">
              <span className="px-4 py-2 rounded-lg bg-primary/20 text-primary border border-primary/30">
                Values Discovery
              </span>
              <ArrowRight className="w-4 h-4 text-muted-foreground hidden md:block" />
              <ArrowDown className="w-4 h-4 text-muted-foreground md:hidden" />
              <span className="px-4 py-2 rounded-lg bg-accent/20 text-accent border border-accent/30">
                Goals
              </span>
              <ArrowRight className="w-4 h-4 text-muted-foreground hidden md:block" />
              <ArrowDown className="w-4 h-4 text-muted-foreground md:hidden" />
              <span className="px-4 py-2 rounded-lg bg-warning/20 text-warning border border-warning/30">
                AI Plan Generation
              </span>
              <ArrowRight className="w-4 h-4 text-muted-foreground hidden md:block" />
              <ArrowDown className="w-4 h-4 text-muted-foreground md:hidden" />
              <span className="px-4 py-2 rounded-lg bg-success/20 text-success border border-success/30">
                Habits
              </span>
              <ArrowRight className="w-4 h-4 text-muted-foreground hidden md:block" />
              <ArrowDown className="w-4 h-4 text-muted-foreground md:hidden" />
              <span className="px-4 py-2 rounded-lg bg-secondary text-foreground border border-border">
                Progress Tracking
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
